import { useTranslation } from "react-i18next";
import { IconPencil, IconRefresh } from "@boligportal/bricks-icons";
import { Button, Dialog } from "@boligportal/bricks-ui";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { accessAddressQueries } from "~/api/access-address/queries";
import { AlertState } from "~/components/bricks/AlertState";
import { actionOnMissingAccess } from "~/utils/access";
import { popoverStopPropagation } from "~/utils/modals";
import { UnitList } from "../-components/UnitList/UnitList";
import { CurrentRentCard } from "./-components/CurrentRentCard";
import { RecommendedRentCard } from "./-components/RecommendedRentCard";
import { UnitHeader } from "./-components/UnitList/UnitHeader/UnitHeader";
import { UnitReport } from "./-components/UnitList/UnitReport/UnitReport";
import { CurrentRentForm } from "./-components/dialogs/CurrentRentForm";
import {
  currentRentDialogOpenAtom,
  focusedUnitIdAtom,
} from "./-state/current-rent-atoms";

const ErrorComponent = (props: ErrorComponentProps) => {
  return (
    <AlertState
      headline={props.error.name}
      description={props.error.message}
    />
  );
};

export const Route = createFileRoute(
  "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/rent-analysis/{-$unitId}",
)({
  beforeLoad: ({ context: { authClient }, params }) => {
    actionOnMissingAccess(authClient.user, "property-check", () => {
      throw redirect({
        to: "/ejendomstjek/address/$accessAddressId/{-$placeId}/overview/{-$unitId}",
        params,
      });
    });
  },
  component: RouteComponent,
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("rent-analysis");

    const title = i18n.t("rent-analysis:meta.title");

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { t } = useTranslation(["rent-analysis"]);
  const { accessAddressId, placeId, unitId } = Route.useParams();

  const {
    data: unitsData,
    isLoading: isLoadingUnits,
    error: unitsError,
    refetch: refetchUnits,
    isFetching: isFetchingUnits,
  } = useQuery({
    ...accessAddressQueries.units(accessAddressId, placeId),
    throwOnError: true,
  });

  const [isDialogOpen, setIsDialogOpen] = useAtom(currentRentDialogOpenAtom);
  const focusedUnitId = useAtomValue(focusedUnitIdAtom);

  const refetch = () => {
    void refetchUnits();
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-1 flex-col gap-4">
            <RecommendedRentCard
              accessAddressId={accessAddressId}
              placeId={placeId}
            />
          </div>
          <div className="flex flex-1 flex-col gap-4">
            <CurrentRentCard
              accessAddressId={accessAddressId}
              placeId={placeId}
            />
          </div>
        </div>

        <hr />
        <div className="-mb-3 flex items-center justify-end">
          <Button
            size="sm"
            color="primary-administration"
            variant="text"
            onClick={() => setIsDialogOpen(true)}
          >
            <IconPencil />
            {t("rent-analysis:current_rent.edit_rent_cta")}
          </Button>
        </div>
        <UnitList
          items={unitsData?.units}
          isLoading={isLoadingUnits}
          getItemId={(unit) => unit.unitId}
          currentItemId={unitId}
          isItemExpandable={(unit) => unit.isResidential}
          renderHeader={(unit, opts) => (
            <UnitHeader
              unit={unit}
              accessAddressId={accessAddressId}
              {...opts}
            />
          )}
          renderExpanded={(unit) => (
            <UnitReport
              unit={unit}
              accessAddressId={accessAddressId}
              placeId={placeId}
            />
          )}
        />
      </div>
      <CurrentRentForm
        accessAddressId={accessAddressId}
        placeId={placeId}
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        focusedUnitId={focusedUnitId}
      />
      <Dialog.Root
        size="sm"
        open={unitsError !== null && !isFetchingUnits}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner onClick={popoverStopPropagation}>
          <Dialog.Content>
            <Dialog.CloseTrigger className="hidden" />
            <Dialog.Body className="flex flex-col items-center">
              <AlertState
                headline={t("rent-analysis:errors.load_units_failed.title")}
                description={t("rent-analysis:errors.load_units_failed.text")}
                renderPrimaryAction={() => (
                  <Button
                    color="primary-administration"
                    onClick={refetch}
                  >
                    <IconRefresh />
                    {t("rent-analysis:errors.load_units_failed.reload_cta")}
                  </Button>
                )}
                renderSecondaryAction={() => (
                  <Button
                    asChild
                    color="primary-administration"
                  >
                    <Link to="/ejendomstjek">
                      {t("rent-analysis:errors.load_units_failed.go_back_cta")}
                    </Link>
                  </Button>
                )}
              />
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
}
